/**
 * apps/command API access.
 *
 * Deliberately the same shape as `apps/admin/src/api/client.ts`, because it is
 * the same session on the same origin (§2.1): the `__Host-nvadmin` cookie,
 * HttpOnly and Secure, which JS never reads or writes. `credentials: 'include'`
 * is the entire client-side auth story.
 *
 * Every request shape comes from `packages/api-types`, GENERATED from
 * `contracts/openapi.yaml` — so a contract change that breaks this panel breaks
 * its typecheck rather than an operator's shift.
 */
import { createNavoiyClient, isProblem, type NavoiyClient, type Problem } from '@navoiy/api-types';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export function apiBaseUrl(): string {
  return new URL(API_BASE, window.location.origin).toString().replace(/\/$/, '');
}

/**
 * The operator socket's URL, derived from the page origin rather than
 * configured.
 *
 * §2.1's same-origin decision is what makes this possible: the cookie the
 * socket authenticates with is `__Host-` prefixed and cannot cross origins, so
 * a configurable realtime host would be a configuration that can only ever be
 * wrong. `https:` → `wss:` is the only transformation.
 */
export function realtimeUrl(path: string): string {
  const url = new URL(path, window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

let client: NavoiyClient | null = null;

export function api(): NavoiyClient {
  if (client) return client;
  client = createNavoiyClient({
    baseUrl: apiBaseUrl(),
    // Dev and preview run same-origin over http; production is https-only (§10.3).
    allowInsecure: window.location.protocol === 'http:',
    credentials: 'include',
  });
  return client;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;

  constructor(status: number, problem: Problem | null) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

/**
 * Unwraps an openapi-fetch result, throwing `ApiError` on anything that is not
 * a 2xx with a body.
 *
 * A 401 has to bounce the operator to the login screen whatever body came with
 * it, which is why `status` is carried separately from the problem document.
 */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.data !== undefined && result.response.ok) return result.data;
  const problem = isProblem(result.error) ? result.error : null;
  throw new ApiError(result.response.status, problem);
}

export interface UploadedMbtMedia {
  media_id: string;
  url: string;
  kind: 'image' | 'video';
  width?: number;
  height?: number;
  /** The video's own length, read from the file server-side on ingest. */
  duration_seconds?: number;
}

/**
 * §6.4's image/video upload, done with `fetch` and `FormData` rather than
 * through the generated client.
 *
 * The generated client serialises JSON bodies; a multipart upload of a 24 MiB
 * video is not that, and forcing it through would mean base64 in a JSON field —
 * a third more bytes over a mahalla line, for a file that is racing §6.5's
 * sixty seconds. The auth story is unchanged: same origin, same `__Host-`
 * cookie, `credentials: 'include'`, and the browser sets the multipart boundary
 * itself (setting Content-Type by hand here is the classic way to break it).
 */
export async function uploadMbtMedia(file: File): Promise<UploadedMbtMedia> {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${apiBaseUrl()}/mbt/media`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!response.ok) {
    let problem: Problem | null = null;
    try {
      const body: unknown = await response.json();
      problem = isProblem(body) ? body : null;
    } catch {
      // A 413 from a proxy has no JSON body at all — the status is the message.
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as UploadedMbtMedia;
}
